import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { Report, ReportObject, ReportObjectType } from "@bpm/models";
import { PagedData } from "@core/models/datables";
import { environment } from "environments/environment";

@Injectable({ providedIn: "root" })
export class ReportService {
  //EndPoint para controlador de este objeto en backend
  private reportApiURL = `${environment.bpmApiUrl}/api/reports`;

  constructor(private _http: HttpClient) {}

  /**
   * Obtiene un listado de reportes páginados
   * @param {number} page Indica el número de página
   * @param {number} quantity Indica la cantidad que se desea por página
   * @returns Devuelve un listado de reportes páginados
   */
  getAllPaged(page: number, quantity: number) {
    return this._http
      .get<PagedData<Report>>(`${this.reportApiURL}/page/${page}/${quantity}`)
      .pipe(
        map((response: any) => response as PagedData<Report>),
        catchError((e) => {
          return throwError(e);
        })
      );
  }

  /**
   * Obtiene los reportes activos filtrados por el nombre del tipo de reporte
   * (case-insensitive). Ej: getByType('Dashboard')
   * @param {string} typeName Nombre del tipo de reporte
   * @returns Lista de reportes
   */
  getByType(typeName: string) {
    return this._http
      .get<Report[]>(`${this.reportApiURL}/by-type/${encodeURIComponent(typeName)}`)
      .pipe(
        map((response: any) => response as Report[]),
        catchError((e) => {
          return throwError(e);
        })
      );
  }

  /**
   * Obtiene el total de reportes activos filtrados por el nombre del tipo de
   * reporte (case-insensitive). Ej: getCountByType('Dashboard')
   * @param {string} typeName Nombre del tipo de reporte
   * @returns Total de reportes
   */
  getCountByType(typeName: string) {
    return this._http
      .get<number>(`${this.reportApiURL}/by-type/${encodeURIComponent(typeName)}/count`)
      .pipe(
        map((response: any) => response as number),
        catchError((e) => {
          return throwError(e);
        })
      );
  }

  /**
   * Obtiene un reporte por medio de su id
   * @param {number} reportId Indica el id del reporte
   * @returns Devuelve un reporte por medio de su id
   */
  getById(reportId: number) {
    return this._http.get<Report>(`${this.reportApiURL}/${reportId}`).pipe(
      map((response: any) => response as Report),
      catchError((e) => {
        return throwError(e);
      })
    );
  }

  /**
   * Obtiene un reporte por medio del id del menú
   * @param {number} menuId Indica el id del menú
   * @returns Devuelve un reporte por medio del id del menú
   */
  getByMenuId(menuId: number) {
    return this._http.get<Report>(`${this.reportApiURL}/${menuId}/menu`).pipe(
      map((response: any) => response as Report),
      catchError((e) => {
        return throwError(e);
      })
    );
  }

  /**
   * Función que sirve para guardar un reporte
   * @param {Report} report Objeto de tipo reporte
   * @returns Devuelve un reporte creado
   */
  create(report: Report) {
    return this._http.post<Report>(`${this.reportApiURL}`, report).pipe(
      map((response: any) => response.report as Report),
      catchError((e) => {
        return throwError(e);
      })
    );
  }

  /**
   * Función que sirve para actualizar un reporte
   * @param {Report} report Objeto de tipo reporte
   * @returns Devuelve un reporte actualizado
   */
  update(report: Report) {
    return this._http
      .put<Report>(`${this.reportApiURL}/${report.id}`, report)
      .pipe(
        map((response: any) => response.report as Report),
        catchError((e) => {
          return throwError(e);
        })
      );
  }

  /**
   * Función que sirve para cambiar de estado a eliminado
   * @param reportId Indica el id del reporte
   * @returns Devuelve un reporte eliminado
   */
  delete(reportId: number) {
    return this._http
      .delete<Report>(`${this.reportApiURL}/${reportId}`)
      .pipe(
        map((response: any) => response.report as Report),
        catchError((e) => {
          return throwError(e);
        })
      );
  }

  /**
   * Obtiene las asignaciones (usuarios/grupos) que pueden ver el reporte
   * @param {number} reportId Indica el id del reporte
   * @returns Listado de asignaciones del reporte
   */
  getObjects(reportId: number) {
    return this._http
      .get<ReportObject[]>(`${this.reportApiURL}/${reportId}/objects`)
      .pipe(
        map((response: any) => response as ReportObject[]),
        catchError((e) => {
          return throwError(e);
        })
      );
  }

  /**
   * Agrega un usuario o grupo a la lista de objetos que pueden ver el reporte
   * @param {number} reportId Indica el id del reporte
   * @param {string | number} objectId Id del usuario o grupo
   * @param {ReportObjectType} type Tipo de objeto (USER/GROUP)
   * @returns Devuelve la asignación creada
   */
  addObject(reportId: number, objectId: string | number, type: ReportObjectType) {
    return this._http
      .post<ReportObject>(`${this.reportApiURL}/${reportId}/objects`, { objectId: `${objectId}`, type })
      .pipe(
        map((response: any) => response.reportObject as ReportObject),
        catchError((e) => {
          return throwError(e);
        })
      );
  }

  /**
   * Elimina un usuario o grupo de la lista de objetos que pueden ver el reporte
   * @param {number} reportId Indica el id del reporte
   * @param {string | number} objectId Id del usuario o grupo
   * @param {ReportObjectType} type Tipo de objeto (USER/GROUP)
   * @returns Devuelve la respuesta de la eliminación
   */
  removeObject(reportId: number, objectId: string | number, type: ReportObjectType) {
    return this._http
      .delete(`${this.reportApiURL}/${reportId}/objects/${type}/${objectId}`)
      .pipe(
        catchError((e) => {
          return throwError(e);
        })
      );
  }
}
